import React, { useRef, useState } from 'react';
import { Alert, Button, TextField, Typography } from '@mui/material';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { resourceCache } from 'use-async-resource';
import { OpeningSettlement } from '../models/opening-balances';
import { reverseOpeningAllocation } from '../utils/opening-balances-api';
import { useGetDocument, useGetInvoices } from '../utils/clients-hooks';
import { useGetAllDocuments, useGetAllTurnovers } from '../utils/users-hooks';

export const OpeningAllocationReversal = ({ settlement, onReversed, onDialogChange }: { settlement: OpeningSettlement; onReversed: () => void; onDialogChange?: (open: boolean) => void }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'openingBalances' });
    const { t: common } = useTranslation('translation', { keyPrefix: 'common' });
    const [open, setOpen] = useState(false);
    const changeOpen = (value: boolean) => { setOpen(value); onDialogChange?.(value); };
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(false);
    const submitting = useRef(false);
    const actionLabel = settlement.creditId ? 'reverseAllocation' : 'reversePayment';
    const { register, handleSubmit, reset } = useForm<{ reason: string }>();
    const submit = handleSubmit(async ({ reason }) => {
        if (submitting.current || !reason.trim()) return;
        submitting.current = true; setBusy(true); setError(false);
        try {
            await reverseOpeningAllocation(settlement.id, reason.trim());
            resourceCache(useGetAllTurnovers).clear(); resourceCache(useGetInvoices).clear();
            resourceCache(useGetAllDocuments).clear(); resourceCache(useGetDocument).clear();
            changeOpen(false); onReversed();
        } catch { setError(true); }
        finally { submitting.current = false; setBusy(false); }
    });
    if (settlement.reversedAt) return <Typography variant='body2'>{t('reversedSummary', { date: settlement.reversedAt, actor: settlement.reversedBy, reason: settlement.reversalReason })}</Typography>;
    return <>
        <Button size='small' color='error' disabled={!settlement.reversible} onClick={() => { reset({ reason: '' }); setError(false); changeOpen(true); }}>{t(actionLabel)}</Button>
        <Modal show={open} onHide={() => !busy && changeOpen(false)} centered backdrop='static' keyboard={!busy}>
            <Modal.Header><Modal.Title>{t(actionLabel)}</Modal.Title></Modal.Header>
            <form onSubmit={submit}>
                <Modal.Body>
                    <Typography>{t(settlement.creditId ? 'reversalHint' : 'paymentReversalHint')}</Typography>
                    {error && <Alert severity='error'>{t('reversalFailed')}</Alert>}
                    <TextField autoFocus required fullWidth multiline label={t('reversalReason')} inputProps={{ maxLength: 2000 }} {...register('reason', { required: true, validate: value => !!value.trim() })} sx={{ mt: 2 }} />
                </Modal.Body>
                <Modal.Footer><Button disabled={busy} onClick={() => changeOpen(false)}>{common('close')}</Button><Button color='error' disabled={busy} type='submit'>{t(actionLabel)}</Button></Modal.Footer>
            </form>
        </Modal>
    </>;
};
